import toast from 'react-hot-toast';
import i18n from '@/i18n';
import { ErrorPhaseManager } from './errorPhase.manager';
import { healthCheckService } from './healthCheck.service';

const TOAST_ID_PREFIX = 'error-coord-';

/**
 * Singleton that tracks per-endpoint API failures with toast UI.
 *
 * Uses ErrorPhaseManager per source key (endpoint) with short durations:
 *   Phase 1 (silent): 5s — nothing shown
 *   Phase 2 (warning): 10s — toast.loading
 *   Phase 3 (error): toast.error
 *
 * Defers to HealthMonitor when it's active (full-page errors take priority).
 */
class ErrorCoordinator {
  private sources = new Map<string, ErrorPhaseManager>();
  private healthMonitorActive = false;

  handleServerError(error: unknown, sourceKey: string): void {
    // Skip if HealthMonitor already showing full-page error
    if (this.healthMonitorActive) return;

    // Skip auth errors (handled by interceptor)
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) return;

    // Get or create phase manager for this source
    if (!this.sources.has(sourceKey)) {
      const toastId = TOAST_ID_PREFIX + sourceKey;
      const manager = new ErrorPhaseManager({
        phase1Duration: 5_000,
        phase2Duration: 10_000,
        onPhaseChange: (phase) => {
          if (this.healthMonitorActive) return;
          if (phase === 'warning') {
            toast.loading(
              i18n.t('connectionError.connectingDots', { ns: 'common' }),
              { id: toastId }
            );
          } else if (phase === 'error') {
            toast.error(
              i18n.t('connectionError.backend.title', { ns: 'common' }),
              { id: toastId, duration: Infinity }
            );
            // Trigger a health check to see if it's a system-wide issue
            this.triggerHealthCheck();
          }
        },
      });
      this.sources.set(sourceKey, manager);
      manager.start();
    }
  }

  handleRecovery(sourceKey: string): void {
    const manager = this.sources.get(sourceKey);
    if (manager) {
      manager.reset();
      this.sources.delete(sourceKey);
      toast.dismiss(TOAST_ID_PREFIX + sourceKey);
    }
  }

  setHealthMonitorActive(active: boolean): void {
    this.healthMonitorActive = active;
    if (active) {
      // HealthMonitor takes over — dismiss all toasts
      for (const [key, manager] of this.sources) {
        manager.reset();
        toast.dismiss(TOAST_ID_PREFIX + key);
      }
      this.sources.clear();
    }
  }

  triggerHealthCheck(): void {
    healthCheckService.checkHealth();
  }
}

export const errorCoordinator = new ErrorCoordinator();
